import moment, { Moment } from "moment-timezone";
import { IEffectiveFacilityPolicyFloorPlanDto, IFacilityPolicyEffect } from "types/interfaces/facility-policy";
import { FacilityPolicyScope } from "types/enums/facility-policy";
import { ISO_8601_DATE_TIME_FORMAT } from "types/constants";
import { MomentTimeRange } from "types/interfaces/time-range";

import { IEffectiveFacilityPolicy } from "types/interfaces/facility-policy/facility-policy-response";

/**
 * Maps string date to the facility start and end time strings in ISO 8601 format.
 */
export function mapStringDateToFacilityStartEndTime(
  selectedDate: string,
  timezone: string,
): { start: string; end: string } {
  return {
    // Interpret the date string in the given timezone so "start/end of day"
    // match that local day, not the browser/device timezone.
    start: moment.tz(selectedDate, timezone).startOf("day").utc().format(ISO_8601_DATE_TIME_FORMAT),
    end: moment.tz(selectedDate, timezone).endOf("day").utc().format(ISO_8601_DATE_TIME_FORMAT),
  };
}

/**
 * Maps moment date to the facility start and end time strings in ISO 8601 format.
 */
export function mapDateToFacilityStartEndTime(selectedDate: Moment, timezone: string): { start: string; end: string } {
  return {
    start: selectedDate.clone().tz(timezone).startOf("day").utc().format(ISO_8601_DATE_TIME_FORMAT),
    end: selectedDate.clone().tz(timezone).endOf("day").utc().format(ISO_8601_DATE_TIME_FORMAT),
  };
}

/**
 * Maps timestamps to the facility start and end time strings in ISO 8601 format.
 * Removes milliseconds to match the format used by other mappers (YYYY-MM-DDTHH:mm:ssZ).
 */
export function mapTimestampsToFacilityStartEndTime(
  startTime: number,
  endTime: number,
): { start: string; end: string } {
  return {
    start: new Date(startTime).toISOString().replace(/\.\d{3}Z$/, "Z"),
    end: new Date(endTime).toISOString().replace(/\.\d{3}Z$/, "Z"),
  };
}

/**
 * Maps MomentTimeRange to facility start and end time strings in ISO 8601 format.
 * Converts the Moment objects (which may be in office timezone) to UTC before formatting.
 */
export function mapMomentTimeRangeToFacilityStartEndTime(timeRange: MomentTimeRange): { start: string; end: string } {
  return {
    start: timeRange.startTime.clone().utc().format(ISO_8601_DATE_TIME_FORMAT),
    end: timeRange.endTime.clone().utc().format(ISO_8601_DATE_TIME_FORMAT),
  };
}

/**
 * Maps effective facility policies to floor plan DTOs.
 * Extracts the reason and hide flag from booking scope effects.
 */
export function mapEffectiveFacilityPoliciesToFloorPlanDto(
  policies: IEffectiveFacilityPolicy[] | null | undefined,
): IEffectiveFacilityPolicyFloorPlanDto[] {
  if (!policies?.length) {
    return [];
  }

  return policies.map((policy) => ({
    zoneUuid: policy.zoneUuid,
    resourceUuid: policy.resourceUuid,
    startTime: policy.startTime,
    endTime: policy.endTime,
    reason:
      policy.effects.find((e: IFacilityPolicyEffect) => e.scope === FacilityPolicyScope.BOOKING)?.options?.reason || "",
    hide: !!policy.effects.find(
      (e: IFacilityPolicyEffect) => e.scope === FacilityPolicyScope.BOOKING && e.options?.hide,
    ),
  }));
}
